import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

/**
 * NotchSegmented — a controlled segmented selector in the hex-notch language
 * (the app's This-Workspace / All-Workspaces and Fast / Pro toggles). A notched
 * track (`nx-rim` keyline, since a real border is sliced at the notch) holds the
 * options; the selected one is a notched accent segment (`nx-t` + `--primary`),
 * the rest are quiet text. Radio semantics (`role="radiogroup"` / `aria-checked`)
 * + roving arrow-key focus.
 */
const trackVariants = cva("relative inline-flex items-center bg-card", {
  variants: {
    size: {
      sm: "h-8 gap-0.5 p-0.5 text-[12px]",
      default: "h-9 gap-1 p-1 text-[13px]",
      lg: "h-11 gap-1 p-1 text-sm",
    },
  },
  defaultVariants: { size: "default" },
});

export interface NotchSegmentedOption {
  value: string;
  label: React.ReactNode;
  /** Optional leading glyph. */
  icon?: React.ReactNode;
}

export interface NotchSegmentedProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof trackVariants> {
  options: ReadonlyArray<NotchSegmentedOption>;
  value: string;
  onValueChange: (value: string) => void;
  /** Track notch size. Default `m`. */
  notch?: NxSize;
  /** Accessible name for the group. */
  "aria-label"?: string;
}

export const NotchSegmented = React.forwardRef<HTMLDivElement, NotchSegmentedProps>(
  ({ className, options, value, onValueChange, size, notch = "m", ...rest }, ref) => {
    const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!dir) return;
      e.preventDefault();
      const i = options.findIndex((o) => o.value === value);
      const next = (i + dir + options.length) % options.length;
      onValueChange(options[next].value);
      refs.current[next]?.focus();
    };

    return (
      <div
        ref={ref}
        role="radiogroup"
        onKeyDown={onKeyDown}
        className={cn(nx[notch], trackVariants({ size }), className)}
        {...rest}
      >
        {options.map((o, i) => {
          const selected = o.value === value;
          return (
            <button
              key={o.value}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onValueChange(o.value)}
              className={cn(
                "relative inline-flex h-full cursor-pointer items-center gap-1.5 whitespace-nowrap px-3 font-medium",
                "transition-colors duration-150 [&_svg]:size-4 [&_svg]:shrink-0",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                nx.t, // each segment carries the notch; the selected one fills with accent
                selected
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {o.icon}
              {o.label}
            </button>
          );
        })}
        {/* track keyline — continuous around the notch (a real border is sliced) */}
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0 [--nx-rim:hsl(var(--border))]"
          style={{ clipPath: NX_RING_CLIP[notch] }}
        />
      </div>
    );
  },
);

NotchSegmented.displayName = "NotchSegmented";
